
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState('home');
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { id: 'home', label: 'HOME' },
    { id: 'about', label: 'ÜBER MICH' },
    { id: 'portfolio', label: 'PORTFOLIO' },
    { id: 'contact', label: 'KONTAKT' }
  ];

  const scrollToSection = (sectionId: string) => {
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } else {
      // We're already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    
    setActiveItem(sectionId);
    setIsMenuOpen(false);
  };

  const navigateToHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      // Already on home, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setActiveItem('home');
  };

  useEffect(() => {
    // Only set up scroll listener on home page
    if (location.pathname === '/') {
      const handleScroll = () => {
        const sections = ['home', 'about', 'portfolio', 'contact'];
        const scrollPosition = window.scrollY + 100;

        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetHeight = element.offsetHeight;
            
            if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
              setActiveItem(sectionId);
              break;
            }
          }
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [location.pathname]);

  return (
    <nav className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-6">
      <div 
        ref={navRef}
        className="glass-effect rounded-full px-6 py-3 shadow-2xl"
        style={{
          background: 'rgba(255, 255, 255, 0.15)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(199, 161, 122, 0.2)',
        }}
      >
        <div className="flex items-center space-x-8">
          {/* Animated Logo */}
          <button 
            onClick={navigateToHome}
            className="flex items-center space-x-2 text-lumaz-primary font-bold text-lg hover:text-lumaz-secondary transition-all duration-300 cursor-pointer group"
          >
            <div className="w-10 h-10 relative overflow-hidden rounded-full border-2 border-lumaz-light/30 group-hover:border-lumaz-secondary/50 transition-all duration-300">
              <img
                src="https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/0e8d254e41f61d54f0cf58ba92d1dc38.jfif"
                alt="Dion Bamme Logo"
                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-lumaz-secondary/20 to-lumaz-light/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
            </div>
            <span className="hidden sm:block" style={{ fontFamily: '"Pacifico", serif' }}>Dion Bamme</span>
          </button>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeItem === item.id && location.pathname === '/'
                    ? 'text-white bg-lumaz-secondary shadow-lg' 
                    : 'text-lumaz-primary/80 hover:text-lumaz-primary hover:bg-white/10'
                }`}
              >
                <span className="relative z-10">{item.label}</span>
                {activeItem === item.id && location.pathname === '/' && (
                  <div className="absolute inset-0 bg-lumaz-secondary rounded-full shadow-lg animate-pulse"></div>
                )}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-lumaz-primary hover:text-lumaz-secondary cursor-pointer p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center space-y-1">
              <div className={`w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></div>
              <div className={`w-5 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></div>
            </div>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 mt-2 glass-effect rounded-2xl shadow-2xl overflow-hidden">
            <div className="py-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-6 py-3 text-sm font-medium transition-all duration-300 cursor-pointer ${
                    activeItem === item.id && location.pathname === '/'
                      ? 'text-white bg-lumaz-secondary/90' 
                      : 'text-lumaz-primary/80 hover:text-lumaz-primary hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
